// ============================================================================
// PublicFacturaForm — página PÚBLICA (sin login) para que el cliente final
// complete sus propios datos de facturación electrónica desde el celular.
//
// El cajero le muestra un QR o le manda el link por WhatsApp; el cliente abre
// esta página, llena el formulario y queda creado/actualizado en `clientes`.
// Luego el cajero refresca su lista (más reciente primero) y lo selecciona.
//
// Se monta como ruta pública en App.tsx (/f/factura), fuera del gate de login.
// ============================================================================
import { useMemo, useState, type CSSProperties } from 'react';
import { crearClientePublico } from '../../api/clientes';
import { validarIdentificacion, digitoVerificacionNit } from '../../lib/identificacion';
import { SalonikaTomIcon } from './ui/brand-logo';

const input: CSSProperties = {
  padding: '13px 14px',
  background: 'var(--surface)',
  border: '1.5px solid var(--line-2)',
  borderRadius: 'var(--r-sm)',
  fontSize: 16, // 16px evita el zoom automático de iOS al enfocar
  fontWeight: 600,
  color: 'var(--ink)',
  width: '100%',
  outline: 'none',
  fontFamily: 'inherit',
};
const lab: CSSProperties = {
  fontSize: 12.5, fontWeight: 800, letterSpacing: '.04em',
  textTransform: 'uppercase', color: 'var(--muted-ink)', marginBottom: 6, display: 'block',
};

export function PublicFacturaForm() {
  const [tipoDoc, setTipoDoc] = useState('13');
  const [num, setNum] = useState('');
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [direccion, setDireccion] = useState('');
  const [respFiscal, setRespFiscal] = useState('R-99-PN');
  const [enviando, setEnviando] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const val = useMemo(() => validarIdentificacion(tipoDoc, num), [tipoDoc, num]);
  const tipoPersona = tipoDoc === '31' ? '1' : '2'; // NIT = jurídica, resto = natural
  const puede = val.ok && !!nombre.trim() && !enviando;

  const enviar = async () => {
    if (!puede) return;
    setEnviando(true); setErr(null);
    try {
      const ident = num.replace(/[.\s-]/g, '');
      const dv = tipoDoc === '31' ? digitoVerificacionNit(ident) : undefined;
      await crearClientePublico({
        tipo_documento: tipoDoc,
        identificacion: ident,
        dv,
        tipo_persona: tipoPersona,
        nombre: nombre.trim(),
        email: email.trim() || undefined,
        telefono: telefono.trim() || undefined,
        direccion: direccion.trim() || undefined,
        responsabilidad_fiscal: respFiscal,
      });
      setOk(true);
    } catch (e: any) {
      setErr(e?.message || 'No se pudo guardar. Intentá de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center"
      style={{ background: 'var(--blue-grad)', padding: '24px 16px 40px' }}
    >
      <div className="w-full" style={{ maxWidth: 440 }}>
        {/* Encabezado de marca */}
        <div className="flex flex-col items-center text-center" style={{ marginBottom: 18 }}>
          <SalonikaTomIcon size={64} style={{ marginBottom: 12 }} />
          <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: 0 }}>Datos para tu factura</h1>
          <p style={{ color: 'rgba(255,255,255,.85)', fontSize: 14, fontWeight: 600, marginTop: 4 }}>
            Salonika · Factura electrónica
          </p>
        </div>

        {ok ? (
          <div className="sk-modal-shell" style={{ background: 'var(--surface)', padding: 28, textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', margin: '0 auto 14px',
              background: 'var(--ok-bg)', color: 'var(--ok-ink)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, fontWeight: 800,
            }}>✓</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink)', margin: 0 }}>¡Listo!</h2>
            <p style={{ color: 'var(--muted-ink)', fontSize: 14.5, fontWeight: 600, marginTop: 8 }}>
              Tus datos quedaron guardados. Avisale al cajero para que genere tu factura electrónica.
            </p>
            <button
              type="button"
              onClick={() => { setOk(false); setNum(''); setNombre(''); setEmail(''); setTelefono(''); setDireccion(''); }}
              className="sk-btn sk-btn--ghost"
              style={{ marginTop: 18, minHeight: 44, padding: '10px 18px', fontSize: 14 }}
            >
              Cargar otros datos
            </button>
          </div>
        ) : (
          <div className="sk-modal-shell" style={{ background: 'var(--surface)', padding: 22 }}>
            <div className="flex flex-col" style={{ gap: 14 }}>
              <div className="grid grid-cols-2" style={{ gap: 10 }}>
                <div>
                  <label style={lab}>Tipo de documento</label>
                  <select value={tipoDoc} onChange={(e) => setTipoDoc(e.target.value)} style={input}>
                    <option value="13">Cédula (CC)</option>
                    <option value="31">NIT</option>
                    <option value="22">Cédula extranjería</option>
                    <option value="41">Pasaporte</option>
                  </select>
                </div>
                <div>
                  <label style={lab}>Número</label>
                  <input value={num} onChange={(e) => setNum(e.target.value)} inputMode="numeric"
                    placeholder="Documento" style={input} />
                </div>
              </div>

              {/* DV calculado para NIT */}
              {tipoDoc === '31' && num.trim() && val.ok && (
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted-ink)', marginTop: -6 }}>
                  Dígito de verificación: <b style={{ color: 'var(--ink)' }}>{val.dv}</b>
                </div>
              )}

              <div>
                <label style={lab}>Nombre / Razón social</label>
                <input value={nombre} onChange={(e) => setNombre(e.target.value)}
                  placeholder="Tu nombre completo o el de la empresa" style={input} />
              </div>

              <div>
                <label style={lab}>Correo electrónico</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" inputMode="email"
                  placeholder="Para enviarte la factura" style={input} />
              </div>

              <div className="grid grid-cols-2" style={{ gap: 10 }}>
                <div>
                  <label style={lab}>Teléfono</label>
                  <input value={telefono} onChange={(e) => setTelefono(e.target.value)} inputMode="tel"
                    placeholder="Opcional" style={input} />
                </div>
                <div>
                  <label style={lab}>Tipo de persona</label>
                  <div style={{ ...input, background: 'var(--surface-2)', color: 'var(--muted-ink)' }}>
                    {tipoPersona === '1' ? 'Jurídica' : 'Natural'}
                  </div>
                </div>
              </div>

              <div>
                <label style={lab}>Dirección</label>
                <input value={direccion} onChange={(e) => setDireccion(e.target.value)}
                  placeholder="Dirección de facturación" style={input} />
              </div>

              <div>
                <label style={lab}>Responsabilidad fiscal (IVA)</label>
                <select value={respFiscal} onChange={(e) => setRespFiscal(e.target.value)} style={input}>
                  <option value="R-99-PN">No responsable de IVA (lo más común)</option>
                  <option value="O-13">Responsable de IVA</option>
                </select>
              </div>

              {/* Estado de validación del documento */}
              <div style={{ minHeight: 18, fontSize: 13, fontWeight: 700 }}>
                {err ? <span style={{ color: 'var(--danger)' }}>⚠ {err}</span>
                  : num.trim() === '' ? null
                  : !val.ok ? <span style={{ color: 'var(--danger)' }}>⚠ {val.mensaje}</span>
                  : <span style={{ color: 'var(--ok-ink)' }}>✓ Documento válido</span>}
              </div>

              <button
                type="button"
                onClick={enviar}
                disabled={!puede}
                className="sk-btn sk-btn--primary"
                style={{ minHeight: 50, fontSize: 16, width: '100%', opacity: puede ? 1 : 0.55, cursor: puede ? 'pointer' : 'not-allowed' }}
              >
                {enviando ? 'Guardando…' : 'Enviar mis datos'}
              </button>

              <p style={{ color: 'var(--muted-ink)', fontSize: 12, fontWeight: 600, textAlign: 'center', margin: 0 }}>
                Usamos estos datos solo para emitir tu factura electrónica.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
